import { useEffect, useState } from "react";
import { Phone, MessageCircle, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import MailComposer from "@/components/mail/MailComposer";

/**
 * Iter 253q · Quick Contact Actions
 *
 * Renders 4 icon buttons next to any person name. Each action creates a
 * `meetings` row with the appropriate `mode` + `is_logged=true` so it flows
 * through the Interactions timeline. Only the buttons applicable to the
 * available identifiers (phone / email) are rendered.
 *
 * Props:
 *   - entityType: "lead" | "prospect" | "contact" | "client" | "project" | "brand" | "user"
 *   - entityId:   the row's id (used for back-refs; nullable is OK)
 *   - entityName: display name — appears in the meeting title
 *   - phone:      E.164 number for call + WhatsApp
 *   - email:      email address for email quick send
 *   - size:       "sm" (default, 24px icons) or "md" (28px)
 *   - onLogged:   optional callback(meetingId, channel) after a successful log
 */
export default function QuickContactActions({
  entityType = "contact", entityId = null, entityName = "",
  phone = "", email = "", size = "sm", className = "",
  onLogged,
}) {
  const { activeWorkspaceId } = useAuth();
  const [drawer, setDrawer] = useState(null);   // "whatsapp_interakt" | "email"
  const btnCls = size === "md"
    ? "w-7 h-7 inline-flex items-center justify-center border border-border hover:bg-primary/5 hover:border-primary transition"
    : "w-6 h-6 inline-flex items-center justify-center border border-border hover:bg-primary/5 hover:border-primary transition";
  const iconCls = size === "md" ? "w-3.5 h-3.5" : "w-3 h-3";

  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };
  // Iter 253z-13 · AdvancedTable's row-click uses both onMouseDown and
  // onClick handlers, and Radix Dialog closing can bubble a stale click
  // back onto the underlying row. Stop propagation at every phase.
  const stopAll = (e) => {
    e.stopPropagation();
    if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === "function") {
      e.nativeEvent.stopImmediatePropagation();
    }
  };
  const call = async (e) => {
    stop(e);
    if (!phone) return;
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/calls/click-to-call`, {
          customer_number: phone,
          customer_name: entityName,
          entity_type: entityType,
          entity_id: entityId,
        });
      toast.success(`Calling ${entityName || phone}… you'll ring first.`);
      if (onLogged) onLogged(data?.id, "audio_call");
    } catch (e) {
      const msg = apiErrorMessage(e, "Call failed");
      // Common: MCUBE not configured. Fall back to tel: link.
      if (String(msg).toLowerCase().includes("mcube")) {
        window.open(`tel:${phone}`, "_self");
        toast.info("Opening dialer (MCUBE not configured)");
        return;
      }
      toast.error(msg);
    }
  };

  const waDirect = async (e) => {
    stop(e);
    if (!phone) return;
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/quick-contact/log`, {
          channel: "whatsapp_direct",
          entity_type: entityType, entity_id: entityId, entity_name: entityName,
          phone,
        });
      if (data.wa_direct_url) window.open(data.wa_direct_url, "_blank", "noopener");
      if (onLogged) onLogged(data.meeting_id, "whatsapp");
    } catch (e) { toast.error(apiErrorMessage(e, "WhatsApp link failed")); }
  };

  return (
    <>
      <span className={`inline-flex items-center gap-1 ${className}`}
             data-testid={`quick-contact-${entityId || "row"}`}
             onClick={stopAll} onMouseDown={stopAll} onMouseUp={stopAll} onPointerDown={stopAll}>
        {phone && (
          <button type="button" onClick={call} title={`Call ${phone}`}
                  className={btnCls} data-testid="quick-contact-call">
            <Phone className={iconCls} />
          </button>
        )}
        {phone && (
          <button type="button"
                  onClick={(e) => { stop(e); setDrawer("whatsapp_interakt"); }}
                  title="Send WhatsApp via Interakt (business API)"
                  className={btnCls} data-testid="quick-contact-wa-interakt">
            <MessageCircle className={`${iconCls} text-emerald-600`} />
          </button>
        )}
        {phone && (
          <button type="button" onClick={waDirect}
                  title="Open WhatsApp chat (wa.me link)"
                  className={btnCls} data-testid="quick-contact-wa-direct">
            <Send className={`${iconCls} text-emerald-500`} />
          </button>
        )}
        {email && (
          <button type="button"
                  onClick={(e) => { stop(e); setDrawer("email"); }}
                  title={`Email ${email}`}
                  className={btnCls} data-testid="quick-contact-email">
            <Mail className={iconCls} />
          </button>
        )}
      </span>
      {/* R47 · Email icon now opens the full rich-text MailComposer */}
      {drawer === "email" && (
        <MailComposer
          open
          onOpenChange={(v) => { if (!v) setDrawer(null); }}
          entityType={entityType}
          entityId={entityId}
          defaultTo={email}
          defaultRecipientName={entityName}
          onSent={() => { if (onLogged) onLogged(null, "email"); }}
        />
      )}
      {drawer && drawer !== "email" && (
        <QuickContactDialog
          channel={drawer}
          entityType={entityType}
          entityId={entityId}
          entityName={entityName}
          phone={phone}
          email={email}
          onClose={() => setDrawer(null)}
          onLogged={onLogged}
        />
      )}
    </>
  );
}

function QuickContactDialog({ channel, entityType, entityId, entityName,
                                 phone, email, onClose, onLogged }) {
  const { activeWorkspaceId } = useAuth();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [templateParamsStr, setTemplateParamsStr] = useState("");
  const [headerMediaUrl, setHeaderMediaUrl] = useState("");
  const [headerFilename, setHeaderFilename] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Iter 253z-7 · Interakt template dropdown — fetch approved templates so
  // ops can pick one instead of typing raw names.
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false); // R69j · loading skeleton
  const [templateVars, setTemplateVars] = useState({}); // R69j · per-placeholder inputs {1: "John", 2: "25 Aug"}
  const isEmail = channel === "email";

  // R52.1 · Detect if the selected template requires a media header so
  // we can show the uploader. `header_type` comes from either
  // saved_wa_templates OR interakt_templates_cache (enriched via
  // _merge_with_library in R50.1).
  const matchedTpl = templates.find((t) => (t.name || t.template_name) === templateName);
  const headerType = (matchedTpl?.header_type || "").toUpperCase();
  const needsMedia = ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerType);

  useEffect(() => {
    if (isEmail || !activeWorkspaceId) return;
    // R69j · Unify template list with WA Inbox: merge both library
    // (saved-templates) + Interakt-approved so the picker matches the
    // inbox picker exactly. Loading state shows a placeholder select
    // instead of an empty text box while the fetches race.
    setTemplatesLoading(true);
    Promise.allSettled([
      api.get(`/workspaces/${activeWorkspaceId}/marketing/wa-templates/interakt`),
      api.get(`/workspaces/${activeWorkspaceId}/wa/saved-templates`),
    ]).then(([interaktRes, libRes]) => {
      const interakt = interaktRes.status === "fulfilled" ? (interaktRes.value.data?.rows || []) : [];
      const lib = libRes.status === "fulfilled" ? (libRes.value.data?.rows || libRes.value.data || []) : [];
      // De-dupe by name, prefer interakt-approved entries (they carry
      // latest header_type + body_text from Meta).
      const seen = new Set();
      const merged = [];
      for (const t of [...interakt, ...lib]) {
        const key = t.name || t.template_name;
        if (!key || seen.has(key)) continue;
        seen.add(key);
        merged.push({ ...t, __source: interakt.includes(t) ? "approved" : "library" });
      }
      setTemplates(merged);
    }).finally(() => setTemplatesLoading(false));
  }, [isEmail, activeWorkspaceId]);

  // R69j · Parse {{1}}, {{2}}, {{3}} placeholders from the selected
  // template's body_text. Renders a labelled input per var instead of
  // the confusing comma-separated string.
  const placeholderCount = (() => {
    const bodyText = matchedTpl?.body_text || matchedTpl?.template_body || matchedTpl?.body || "";
    if (!bodyText) return 0;
    const matches = String(bodyText).match(/\{\{\s*(\d+)\s*\}\}/g) || [];
    const nums = matches.map((m) => parseInt(m.replace(/[^\d]/g, ""), 10)).filter((n) => !isNaN(n));
    return nums.length ? Math.max(...nums) : 0;
  })();

  // Reset per-var inputs when template changes.
  useEffect(() => {
    setTemplateVars({});
    setTemplateParamsStr("");
  }, [templateName]);

  // Keep the comma-separated string in sync with per-var inputs so the
  // submit payload stays unchanged (server still receives an array).
  useEffect(() => {
    if (placeholderCount === 0) { setTemplateParamsStr(""); return; }
    const parts = [];
    for (let i = 1; i <= placeholderCount; i++) parts.push(templateVars[i] || "");
    setTemplateParamsStr(parts.join(", "));
  }, [templateVars, placeholderCount]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        channel,
        entity_type: entityType, entity_id: entityId, entity_name: entityName,
        phone: !isEmail ? phone : null,
        email: isEmail ? email : null,
        subject: isEmail ? subject : null,
        body,
        template_name: !isEmail ? (templateName || null) : null,
        template_params: !isEmail && templateParamsStr
          ? templateParamsStr.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        header_media_url: !isEmail ? (headerMediaUrl || null) : null,
        header_filename: !isEmail ? (headerFilename || null) : null,
      };
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/quick-contact/log`, payload);
      if (data.sent) {
        toast.success(isEmail ? "Email sent + logged" : "WhatsApp sent + logged");
      } else {
        // Feb 2026 R6 · Surface Interakt's real error text (e.g. "No approved
        // template found with name '...'", "template variables mismatch")
        // instead of just the opaque `interakt_400` code.
        const prov = data.provider_response || {};
        const bits = [];
        if (prov.reason) bits.push(prov.reason);
        if (prov.provider_status) bits.push(`HTTP ${prov.provider_status}`);
        if (prov.detail) {
          try {
            const parsed = JSON.parse(prov.detail);
            if (parsed?.message) bits.push(parsed.message);
            else bits.push(prov.detail);
          } catch { bits.push(prov.detail); }
        }
        const detailStr = bits.length > 0 ? bits.join(" · ") : (prov.overall || "provider not configured");
        toast.warning(
          (isEmail ? "Email logged but not sent — " : "WhatsApp logged but not sent — ")
          + detailStr,
          { duration: 12000 },
        );
      }
      if (onLogged) onLogged(data.meeting_id, isEmail ? "email" : "whatsapp");
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
    finally { setBusy(false); }
  };

  // Iter 253z-24 · Radix Dialog is DOM-portaled to <body>, but React synthetic
  // events still bubble via the React tree back to the underlying <tr onClick>.
  // Stop propagation at the DialogContent level so clicks inside the dialog
  // (including the native <select> template picker) never re-open Edit Lead.
  const stopBubble = (e) => { e.stopPropagation(); };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-md rounded-none"
        data-testid={`quick-contact-dialog-${channel}`}
        onClick={stopBubble}
        onMouseDown={stopBubble}
        onMouseUp={stopBubble}
        onPointerDown={stopBubble}
        onPointerUp={stopBubble}
      >
        <DialogHeader>
          <DialogTitle>
            {isEmail ? "Send email" : "Send WhatsApp"} · {entityName || (isEmail ? email : phone)}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {isEmail && (
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                     className="rounded-none" data-testid="quick-contact-subject" />
            </div>
          )}
          {!isEmail && (
            <>
              <div>
                <Label className="label-eyebrow mb-1 inline-block">Interakt template</Label>
                {templatesLoading ? (
                  // R69j · Show a disabled loading select instead of an
                  // empty text box that jumps to a dropdown a moment
                  // later.
                  <select disabled className="w-full h-9 border border-border px-2 text-sm bg-muted/40 rounded-none italic text-muted-foreground" data-testid="quick-contact-template-loading">
                    <option>Loading templates…</option>
                  </select>
                ) : templates.length > 0 ? (
                  <select value={templateName}
                          onChange={(e) => setTemplateName(e.target.value)}
                          className="w-full h-9 border border-border px-2 text-sm bg-background rounded-none"
                          data-testid="quick-contact-template-select">
                    <option value="">— Session (free-form · 24h window) —</option>
                    {templates.map((t) => {
                      const n = t.name || t.template_name;
                      // R69j · Media-type icon so users see at a glance
                      // whether a template needs media before selecting.
                      const ht = (t.header_type || "").toUpperCase();
                      const icon = ht === "IMAGE" ? "🖼" : ht === "VIDEO" ? "🎬" : ht === "DOCUMENT" ? "📄" : "";
                      return (
                        <option key={n} value={n}>
                          {icon && `${icon} `}{n}
                          {t.language ? ` · ${t.language}` : ""}
                          {t.category ? ` [${t.category}]` : ""}
                          {t.__source === "library" ? " · library" : ""}
                        </option>
                      );
                    })}
                  </select>
                ) : (
                  <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)}
                          placeholder="Leave blank for session/free-form or type template name"
                          className="rounded-none" data-testid="quick-contact-template" />
                )}
                {!templatesLoading && templates.length === 0 && (
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    No approved templates found — session-message (free-form) will be used.
                  </p>
                )}
              </div>
              {templateName && placeholderCount > 0 && (
                <div className="border border-border bg-muted/20 p-2 space-y-1.5" data-testid="quick-contact-template-vars-panel">
                  <Label className="label-eyebrow text-[10px] block">
                    This template has {placeholderCount} variable{placeholderCount === 1 ? "" : "s"}
                  </Label>
                  {Array.from({ length: placeholderCount }, (_, i) => i + 1).map((n) => (
                    <div key={n} className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-muted-foreground w-10 shrink-0">{`{{${n}}}`}</span>
                      <Input
                        value={templateVars[n] || ""}
                        onChange={(e) => setTemplateVars((prev) => ({ ...prev, [n]: e.target.value }))}
                        placeholder={`Value for variable ${n}`}
                        className="rounded-none h-8 text-xs"
                        data-testid={`quick-contact-tpl-var-${n}`}
                      />
                    </div>
                  ))}
                </div>
              )}
              {/* R52.1 · Media header uploader — shows for templates whose
                    header_type is IMAGE / VIDEO / DOCUMENT (e.g. edutrac_oman_jeet
                    requires VIDEO). Uploads via /wa/media/upload and stores
                    the resulting public URL to send along the template. */}
              {templateName && needsMedia && (
                <div className="border border-primary/40 bg-primary/[0.03] p-2 space-y-1"
                      data-testid="quick-contact-media-header">
                  <Label className="label-eyebrow inline-block text-[10px]">
                    Header · {headerType.toLowerCase()} required
                  </Label>
                  {headerMediaUrl ? (
                    <div className="text-xs flex items-center gap-2">
                      <span className="text-emerald-700 font-semibold">✓ {headerFilename || "media uploaded"}</span>
                      <button type="button" className="text-[10px] underline text-muted-foreground"
                                onClick={() => { setHeaderMediaUrl(""); setHeaderFilename(""); }}
                                data-testid="quick-contact-media-clear">Replace</button>
                    </div>
                  ) : (
                    <label className="inline-flex items-center gap-2 text-xs cursor-pointer border border-dashed border-border p-2 hover:bg-muted/40 w-full">
                      <input type="file"
                              accept={headerType === "VIDEO" ? "video/*" : headerType === "IMAGE" ? "image/*" : "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"}
                              className="hidden"
                              disabled={uploading}
                              onChange={async (e) => {
                                const f = e.target.files?.[0];
                                if (!f) return;
                                setUploading(true);
                                try {
                                  const fd = new FormData();
                                  fd.append("file", f);
                                  const { data } = await api.post(
                                    `/workspaces/${activeWorkspaceId}/wa/media/upload`, fd,
                                    { headers: { "Content-Type": "multipart/form-data" } });
                                  setHeaderMediaUrl(data.public_url || data.url || "");
                                  setHeaderFilename(f.name);
                                  toast.success("Media uploaded");
                                } catch (err) {
                                  toast.error(apiErrorMessage(err, "Upload failed"));
                                } finally { setUploading(false); }
                              }}
                              data-testid="quick-contact-media-file" />
                      {uploading ? "Uploading…" : `Click to upload ${headerType.toLowerCase()}`}
                    </label>
                  )}
                </div>
              )}
            </>
          )}
          <div>
            <Label className="label-eyebrow mb-1 inline-block">
              {isEmail ? "Message body" : (templateName ? "Preview (as Interakt will send)" : "Message text")}
            </Label>
            {!isEmail && templateName ? (
              // R69j · Read-only rendered preview of what the recipient
              // will actually see — placeholders substituted with the
              // per-var inputs above. No editable textarea when a
              // template is chosen (Interakt won't send free-text with
              // approved templates anyway).
              <div
                className="border border-border bg-muted/10 p-3 text-xs whitespace-pre-wrap min-h-[80px] font-mono"
                data-testid="quick-contact-tpl-preview"
              >
                {(() => {
                  const raw = matchedTpl?.body_text || matchedTpl?.template_body || matchedTpl?.body || "";
                  if (!raw) return <span className="text-muted-foreground italic">Template body not available — Interakt will apply your approved template as-is.</span>;
                  let out = String(raw);
                  for (let i = 1; i <= placeholderCount; i++) {
                    const val = templateVars[i] || `{{${i}}}`;
                    out = out.replaceAll(`{{${i}}}`, val);
                    out = out.replaceAll(`{{ ${i} }}`, val);
                  }
                  return out;
                })()}
              </div>
            ) : (
              <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)}
                        className="rounded-none" data-testid="quick-contact-body" />
            )}
          </div>
          <p className="text-[10px] text-muted-foreground italic">
            This action is logged as an Interaction on the {entityType} regardless
            of whether the provider send succeeds.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} data-testid="quick-contact-cancel">Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="quick-contact-submit">
            {busy ? "Sending…" : (isEmail ? "Send email" : "Send WhatsApp")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
